import type Geometry from '@arcgis/core/geometry/Geometry.js';
import { resolve, type Source, SourceSchema } from '@/agent/refs';
import {
  ensureFeatureLayer,
  requireLayerRecord,
  translateAttributes,
} from './shared/layers';
import type { Tool } from './types';

type Format = 'geojson' | 'csv';

type Input = {
  source: Source;
  format: Format;
  filename?: string;
};

const ROW_CAP = 5000;

export const exportFeatures: Tool<Input> = {
  name: 'export_features',
  description:
    'Download a feature_set as GeoJSON or CSV. CSV decodes coded values into readable labels. GeoJSON uses WGS84 (EPSG:4326). Capped at 5000 rows — flags `truncated: true` if the source had more.',
  input_schema: {
    type: 'object',
    properties: {
      source: SourceSchema,
      format: { enum: ['geojson', 'csv'] },
      filename: { type: 'string' },
    },
    required: ['source', 'format'],
    additionalProperties: false,
  },
  execute: async ({ source, format, filename }) => {
    const ref = await resolve(source);
    const layerId =
      ref.feature_set?.layer_id ?? ref.feature?.layer_id;
    if (!layerId) {
      throw new Error(
        'export_features: source must resolve to a feature or feature_set (got source_kind=' +
          ref.source_kind +
          ')',
      );
    }

    const layer = await ensureFeatureLayer(layerId);
    const probe = requireLayerRecord(layerId).probe;
    const oidField = layer.objectIdField || 'objectid';

    const where = buildOidWhere(ref, oidField);
    if (where === '1=0') {
      return { ok: true, filename: '', count: 0, format, empty: true };
    }

    const rs = await layer.queryFeatures({
      where,
      outFields: ['*'],
      returnGeometry: format === 'geojson',
      num: ROW_CAP + 1,
      ...(format === 'geojson' ? { outSpatialReference: { wkid: 4326 } } : {}),
    });

    const truncated = rs.features.length > ROW_CAP;
    const features = truncated ? rs.features.slice(0, ROW_CAP) : rs.features;

    const finalName =
      filename && filename.trim()
        ? ensureExt(filename, format)
        : `${slug(layerId)}-${stamp()}.${format}`;

    if (format === 'csv') {
      const csv = featuresToCsv(features, probe);
      downloadBlob(csv, 'text/csv;charset=utf-8', finalName);
    } else {
      const geojson = featuresToGeoJSON(features, probe, oidField);
      downloadBlob(geojson, 'application/geo+json', finalName);
    }

    return {
      ok: true,
      filename: finalName,
      count: features.length,
      format,
      ...(truncated ? { truncated: true, cap: ROW_CAP } : {}),
    };
  },
};

function buildOidWhere(
  ref: Awaited<ReturnType<typeof resolve>>,
  oidField: string,
): string {
  if (ref.feature_set) {
    const oids = ref.feature_set.objectids;
    if (oids.length === 0) return '1=0';
    return `${oidField} IN (${oids.join(',')})`;
  }
  if (ref.feature) {
    return `${oidField} = ${ref.feature.objectid}`;
  }
  return '1=0';
}

type ProbeFields = ReturnType<typeof requireLayerRecord>['probe'];

function featuresToCsv(
  features: __esri.Graphic[],
  probe: ProbeFields,
): string {
  if (features.length === 0) return '';
  const translated = features.map((f) => translateAttributes(f.attributes ?? {}, probe));
  const columns = Object.keys(translated[0]);
  const lines = [columns.map(escapeCsv).join(',')];
  for (const row of translated) {
    lines.push(columns.map((c) => escapeCsv(row[c])).join(','));
  }
  return lines.join('\n');
}

function escapeCsv(value: unknown): string {
  if (value == null) return '';
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function featuresToGeoJSON(
  features: __esri.Graphic[],
  probe: ProbeFields,
  oidField: string,
): string {
  const out = {
    type: 'FeatureCollection',
    features: features
      .map((f) => featureToGeoJSON(f, probe, oidField))
      .filter((x): x is GeoJSONFeature => x != null),
  };
  return JSON.stringify(out);
}

type GeoJSONFeature = {
  type: 'Feature';
  id?: string | number;
  geometry: GeoJSONGeometry | null;
  properties: Record<string, unknown>;
};

type GeoJSONGeometry =
  | { type: 'Point'; coordinates: number[] }
  | { type: 'MultiPoint'; coordinates: number[][] }
  | { type: 'LineString'; coordinates: number[][] }
  | { type: 'MultiLineString'; coordinates: number[][][] }
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

function featureToGeoJSON(
  f: __esri.Graphic,
  probe: ProbeFields,
  oidField: string,
): GeoJSONFeature | null {
  const properties = translateAttributes(f.attributes ?? {}, probe);
  const idValue = f.attributes?.[oidField];
  const id =
    typeof idValue === 'number' || typeof idValue === 'string' ? idValue : undefined;
  const geometry = arcgisGeometryToGeoJSON(f.geometry as Geometry | null | undefined);
  return {
    type: 'Feature',
    ...(id !== undefined ? { id } : {}),
    geometry,
    properties,
  };
}

function arcgisGeometryToGeoJSON(
  geom: Geometry | null | undefined,
): GeoJSONGeometry | null {
  if (!geom) return null;
  switch (geom.type) {
    case 'point': {
      const p = geom as __esri.Point;
      return { type: 'Point', coordinates: [p.x, p.y] };
    }
    case 'multipoint': {
      const mp = geom as __esri.Multipoint;
      return { type: 'MultiPoint', coordinates: mp.points.map((pt) => [pt[0], pt[1]]) };
    }
    case 'polyline': {
      const pl = geom as __esri.Polyline;
      if (pl.paths.length === 1) {
        return { type: 'LineString', coordinates: pl.paths[0].map((p) => [p[0], p[1]]) };
      }
      return {
        type: 'MultiLineString',
        coordinates: pl.paths.map((path) => path.map((p) => [p[0], p[1]])),
      };
    }
    case 'polygon': {
      const pg = geom as __esri.Polygon;
      const polygons = arcgisRingsToGeoJSONPolygons(pg.rings.map((ring) => ring.map((p) => [p[0], p[1]])));
      if (polygons.length === 0) return null;
      if (polygons.length === 1) {
        return { type: 'Polygon', coordinates: polygons[0] };
      }
      return { type: 'MultiPolygon', coordinates: polygons };
    }
    default:
      return null;
  }
}

// ArcGIS rings: clockwise (signed area > 0 in Y-up coords) = exterior;
// counter-clockwise = hole, attached to the most recent exterior.
// GeoJSON RFC 7946: exterior is CCW, holes are CW. Reverse every ring.
function arcgisRingsToGeoJSONPolygons(rings: number[][][]): number[][][][] {
  const polygons: number[][][][] = [];
  let current: number[][][] | null = null;
  for (const ring of rings) {
    const reversed = ring.slice().reverse();
    if (signedArea(ring) > 0) {
      // exterior in ArcGIS
      current = [reversed];
      polygons.push(current);
    } else if (current) {
      current.push(reversed);
    } else {
      current = [reversed];
      polygons.push(current);
    }
  }
  return polygons;
}

function signedArea(ring: number[][]): number {
  let area = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    area += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return area / 2;
}

function downloadBlob(content: string, mime: string, filename: string): void {
  const blob = new Blob([content], { type: mime });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(href), 0);
}

function ensureExt(name: string, format: Format): string {
  const ext = format === 'csv' ? '.csv' : '.geojson';
  return name.toLowerCase().endsWith(ext) ? name : `${name}${ext}`;
}

function slug(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'export'
  );
}

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}
